// System Configuration API Module
// Consolidates system-related API operations

import { CourierPartner, SystemConfig, SystemStatus } from "@/types";
import { API_ENDPOINTS } from "../../constants/apiEndpoints";
import api from "../axios";
import { handleApiResponse } from "../handleApiResponse";

export interface PaymentQrConfig {
  imageUrl: string;
  updatedAt?: string;
}

// Simple TTL cache using localStorage
const CACHE_PREFIX = "syscfg:";
const defaultTTL = 600; // seconds (10 minutes)

function cacheGet<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Date.now() / 1000 > parsed.expiresAt) {
      localStorage.removeItem(CACHE_PREFIX + key);
      return null;
    }
    return parsed.value as T;
  } catch (e) {
    return null;
  }
}

function cacheSet<T>(key: string, value: T, ttl = defaultTTL) {
  const payload = {
    value,
    expiresAt: Math.floor(Date.now() / 1000) + ttl,
  };
  try {
    localStorage.setItem(CACHE_PREFIX + key, JSON.stringify(payload));
  } catch (e) {
    // ignore
  }
}

function cacheClear(key: string) {
  try {
    localStorage.removeItem(CACHE_PREFIX + key);
  } catch (e) {
    // ignore
  }
}

// Generic system configuration fetcher
export const getSystemConfig = async <T = any>(
  configType: string,
  bustCache = false,
  fallbackData: T[] = []
): Promise<T[]> => {
  const cacheKey = `config_${configType}`;
  if (!bustCache) {
    const cached = cacheGet<T[]>(cacheKey);
    if (cached) return cached;
  }

  const normalizeList = (raw: any): T[] => {
    if (!raw) return fallbackData;
    if (Array.isArray(raw)) return raw;
    // common wrappers
    if (Array.isArray(raw.data)) return raw.data;
    if (Array.isArray(raw[configType])) return raw[configType];
    if (Array.isArray(raw.items)) return raw.items;
    // nested data.data pattern
    if (raw.data && Array.isArray(raw.data.data)) return raw.data.data;
    return fallbackData;
  };

  // Try multiple endpoint patterns for flexibility
  const endpoints = [`/v1/system-config/${configType}`];

  for (const endpoint of endpoints) {
    try {
      const res = await handleApiResponse(api.get(endpoint));
      if (res.success) {
        const list = normalizeList(res.data);
        if (list.length > 0) {
          cacheSet(cacheKey, list);
          return list;
        }
      }
    } catch (e) {
      // continue to next endpoint
    }
  }

  // Cache and return fallback
  cacheSet(cacheKey, fallbackData);
  return fallbackData;
};

// System Configuration Operations
export const getSystemConfigurations = () =>
  handleApiResponse(api.get("/v1/system/config"));

export const updateSystemConfiguration = (
  id: string,
  data: Partial<SystemConfig>
) => handleApiResponse(api.put(`/v1/system/config/${id}`, data));

export const createSystemConfiguration = (data: Omit<SystemConfig, "id">) =>
  handleApiResponse(api.post("/v1/system/config", data));

export const deleteSystemConfiguration = (id: string) =>
  handleApiResponse(api.delete(`/v1/system/config/${id}`));

// System Status Operations
export const getSystemStatuses = () =>
  handleApiResponse(api.get("/v1/system/status"));

export const updateSystemStatus = (id: string, data: Partial<SystemStatus>) =>
  handleApiResponse(api.put(`/v1/system/status/${id}`, data));

export const createSystemStatus = (data: Omit<SystemStatus, "id">) =>
  handleApiResponse(api.post("/v1/system/status", data));

export const deleteSystemStatus = (id: string) =>
  handleApiResponse(api.delete(`/v1/system/status/${id}`));

// Courier Partners - Public API (no auth required)
export const getCourierPartners = async (activeOnly = true) => {
  try {
    const response = await handleApiResponse(
      api.get(API_ENDPOINTS.COURIER_PARTNERS.PUBLIC, {
        params: { active: activeOnly },
      })
    );
    return response.data || [];
  } catch (error) {
    console.error("Error fetching courier partners:", error);
    return [];
  }
};

// Get courier partner options for dropdowns (lightweight)
export const getCourierPartnerOptions = async (
  activeOnly = true,
  bustCache = false
) => {
  const cacheKey = `couriers_options_${activeOnly}`;
  if (!bustCache) {
    const cached = cacheGet<CourierPartner[]>(cacheKey);
    if (cached) return cached;
  }

  try {
    const response = await handleApiResponse(
      api.get(API_ENDPOINTS.COURIER_PARTNERS.OPTIONS, {
        params: { active: activeOnly },
      })
    );
    const options = response.data || [];
    cacheSet(cacheKey, options, 600); // Cache for 10 minutes
    return options;
  } catch (error) {
    console.error("Error fetching courier partner options:", error);
    return [];
  }
};

// Analytics Operations
export const getSystemAnalytics = (dateRange?: {
  start: string;
  end: string;
}) => handleApiResponse(api.get("/v1/analytics/system", { params: dateRange }));

export const getDashboardStats = () =>
  handleApiResponse(api.get(API_ENDPOINTS.ANALYTICS.V1.DASHBOARD));

// Dispatch Statuses
// export const getDispatchStatuses = async (bustCache = false) => {
//   const fallback: any[] = [];
//   return getSystemConfig("dispatch-statuses", bustCache, fallback);
// };

// Payment Modes
export const getPaymentModes = async (bustCache = false) => {
  const fallback: any[] = [];
  return getSystemConfig("payment-modes", bustCache, fallback);
};

// Payment QR Config
export const getPaymentQrConfig = async () =>
  handleApiResponse(
    api.get(API_ENDPOINTS.SYSTEM_CONFIG.PAYMENT_QR, {
      headers: { "Content-Type": "application/json" },
    })
  );

export interface PaymentBankDetails {
  upiId?: string;
  bankName?: string;
  accountNumber?: string;
  ifscCode?: string;
  accountHolderName?: string;
}

export const getPaymentBankDetails = async (): Promise<PaymentBankDetails | null> => {
  const res = await handleApiResponse(
    api.get("/v1/system-config/payment-bank-details")
  );
  return (res.data as PaymentBankDetails | null) ?? null;
};

export const adminSetPaymentBankDetails = async (details: PaymentBankDetails) =>
  handleApiResponse(
    api.patch("/v1/admin/system-config/payment-bank-details", details)
  );

export const adminUploadPaymentQr = async (
  file: File,
  onProgress?: (progress: number) => void
) => {
  const formData = new FormData();
  formData.append("image", file);

  return handleApiResponse(
    api.post(API_ENDPOINTS.SYSTEM_CONFIG.ADMIN.PAYMENT_QR, formData, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (event) => {
        if (event.total) {
          const progress = Math.round((event.loaded / event.total) * 100);
          onProgress?.(progress);
        }
      },
    })
  );
};

// Admin CRUD Operations for Couriers
export const adminGetAllCouriers = async (params?: {
  page?: number;
  limit?: number;
  active?: boolean;
  search?: string;
}) => {
  try {
    const response = await handleApiResponse(
      api.get(API_ENDPOINTS.COURIER_PARTNERS.ADMIN.BASE, { params })
    );
    return response.data || { data: [], pagination: {} };
  } catch (error) {
    console.error("Error fetching all couriers (admin):", error);
    return { data: [], pagination: {} };
  }
};

export const adminGetCourierById = async (id: string) =>
  handleApiResponse(api.get(API_ENDPOINTS.COURIER_PARTNERS.ADMIN.BY_ID(id)));

export const adminCreateCourier = async (formData: any) => {
  // Transform frontend form data to backend payload format
  const payload: any = {
    id: formData.code || formData.value || formData.id,
    value: formData.code || formData.value,
    label: formData.name || formData.label,
    name: formData.name || formData.label,
  };

  // Only include optional fields if they have values
  if (formData.email && formData.email.trim()) {
    payload.email = formData.email.trim();
  }
  if (formData.phone && formData.phone.trim()) {
    payload.phone = formData.phone.trim();
  }
  if (formData.apiUrl && formData.apiUrl.trim()) {
    payload.apiUrl = formData.apiUrl.trim();
  }
  if (formData.active !== undefined) {
    payload.active = formData.active;
  }
  if (formData.tracking !== undefined) {
    payload.tracking = formData.tracking;
  }
  // Include advanced fields if present
  if (formData.cod !== undefined) {
    payload.cod = formData.cod;
  }
  if (formData.maxWeight !== undefined) {
    payload.maxWeight = formData.maxWeight;
  }
  if (
    formData.serviceLevels &&
    Object.keys(formData.serviceLevels).length > 0
  ) {
    payload.serviceLevels = formData.serviceLevels;
  }
  if (
    formData.apiCredentials &&
    Object.keys(formData.apiCredentials).length > 0
  ) {
    payload.apiCredentials = formData.apiCredentials;
  }
  if (
    formData.contact &&
    (formData.contact.name || formData.contact.phone || formData.contact.email)
  ) {
    payload.contact = formData.contact;
  }

  const result = await handleApiResponse(
    api.post(API_ENDPOINTS.COURIER_PARTNERS.ADMIN.BASE, payload)
  );

  // Clear courier cache after successful create
  cacheClear("couriers_options_true");
  cacheClear("couriers_options_false");

  return result;
};

export const adminUpdateCourier = async (id: string, formData: any) => {
  // Transform frontend form data to backend payload format
  const payload: any = {};

  // Map required fields if present
  if (formData.name !== undefined) {
    payload.label = formData.name;
    payload.name = formData.name;
  }
  if (formData.code !== undefined) {
    payload.value = formData.code;
    payload.id = formData.code; // Update id if code changes
  }

  // Only include optional fields if they have values
  if (formData.email !== undefined) {
    payload.email =
      formData.email && formData.email.trim()
        ? formData.email.trim()
        : undefined;
  }
  if (formData.phone !== undefined) {
    payload.phone =
      formData.phone && formData.phone.trim()
        ? formData.phone.trim()
        : undefined;
  }
  if (formData.apiUrl !== undefined) {
    payload.apiUrl =
      formData.apiUrl && formData.apiUrl.trim()
        ? formData.apiUrl.trim()
        : undefined;
  }
  if (formData.active !== undefined) {
    payload.active = formData.active;
  }
  if (formData.tracking !== undefined) {
    payload.tracking = formData.tracking;
  }
  // Include advanced fields if present
  if (formData.cod !== undefined) {
    payload.cod = formData.cod;
  }
  if (formData.maxWeight !== undefined) {
    payload.maxWeight = formData.maxWeight;
  }
  if (formData.serviceLevels !== undefined) {
    payload.serviceLevels = formData.serviceLevels;
  }
  if (formData.apiCredentials !== undefined) {
    payload.apiCredentials = formData.apiCredentials;
  }
  if (formData.contact !== undefined) {
    payload.contact = formData.contact;
  }

  const result = await handleApiResponse(
    api.patch(API_ENDPOINTS.COURIER_PARTNERS.ADMIN.BY_ID(id), payload)
  );

  // Clear courier cache after successful update
  cacheClear("couriers_options_true");
  cacheClear("couriers_options_false");

  return result;
};

export const adminDeleteCourier = async (id: string) => {
  const result = await handleApiResponse(
    api.delete(API_ENDPOINTS.COURIER_PARTNERS.ADMIN.BY_ID(id))
  );

  // Clear courier cache after successful delete
  cacheClear("couriers_options_true");
  cacheClear("couriers_options_false");

  return result;
};

export const adminBulkUpdateCouriers = async (couriers: any[]) =>
  handleApiResponse(
    api.post(API_ENDPOINTS.COURIER_PARTNERS.ADMIN.BULK_UPDATE, { couriers })
  );

export const adminTestCourierConnection = async (id: string) =>
  handleApiResponse(
    api.post(API_ENDPOINTS.COURIER_PARTNERS.ADMIN.TEST_CONNECTION(id))
  );

export const adminGetCourierMetrics = async (id: string, period = "30d") =>
  handleApiResponse(
    api.get(API_ENDPOINTS.COURIER_PARTNERS.ADMIN.METRICS(id), {
      params: { period },
    })
  );

// Admin CRUD Operations for Statuses
export const adminCreateStatus = async (payload: any) =>
  handleApiResponse(api.post("/v1/admin/dispatch-statuses", payload));

export const adminUpdateStatus = async (id: string, payload: any) =>
  handleApiResponse(api.put(`/v1/admin/dispatch-statuses/${id}`, payload));

export const adminDeleteStatus = async (id: string) =>
  handleApiResponse(api.delete(`/v1/admin/dispatch-statuses/${id}`));

// Admin CRUD Operations for Payment Modes
export const adminCreatePaymentMode = async (formData: any) => {
  const payload: any = {
    id: formData.value || formData.id,
    value: formData.value,
    label: formData.label,
  };

  // Optional fields
  if (formData.type && formData.type.trim()) {
    payload.type = formData.type.trim();
  }
  if (formData.gateway && formData.gateway.trim()) {
    payload.gateway = formData.gateway.trim();
  }
  if (formData.active !== undefined) {
    payload.active = formData.active;
  }

  return handleApiResponse(
    api.post("/v1/admin/system-config/payment-modes", payload)
  );
};

export const adminUpdatePaymentMode = async (id: string, formData: any) => {
  const payload: any = {};

  if (formData.label !== undefined) {
    payload.label = formData.label;
  }
  if (formData.value !== undefined) {
    payload.value = formData.value;
    payload.id = formData.value;
  }
  if (formData.type !== undefined) {
    payload.type =
      formData.type && formData.type.trim() ? formData.type.trim() : undefined;
  }
  if (formData.gateway !== undefined) {
    payload.gateway =
      formData.gateway && formData.gateway.trim()
        ? formData.gateway.trim()
        : undefined;
  }
  if (formData.active !== undefined) {
    payload.active = formData.active;
  }

  return handleApiResponse(
    api.patch(`/v1/admin/system-config/payment-modes/${id}`, payload)
  );
};

export const adminDeletePaymentMode = async (id: string) =>
  handleApiResponse(api.delete(`/v1/admin/system-config/payment-modes/${id}`));
